/**
 * Moments in time, by the market's clock.
 *
 * "Today" is the market's day, not the browser's and not UTC's: an operator
 * travelling, or a laptop set to another zone, still reads Tashkent time.
 * Today's moments show the time alone (`14:30`); another day's show the day
 * too (`2-okt, 14:30`); another year's the year as well. A date that is read
 * long after it was written (a birth date, the day a document took effect)
 * carries its year always (`year: "always"`).
 */
import type { Language } from "@/shared/i18n/languages";

/** The zone of the one market there is. When there are two, this becomes an argument everywhere. */
export const MARKET_TIME_ZONE = "Asia/Tashkent";

export interface TimeOptions {
  /** The moment "today" is judged from. Defaults to now. */
  now?: Date;
  timeZone?: string;
  /** When the year is said: only for another year than this one, or always. */
  year?: "other" | "always";
}

const MONTHS: Readonly<Record<Language, readonly string[]>> = {
  uz: ["yan", "fev", "mar", "apr", "may", "iyn", "iyl", "avg", "sen", "okt", "noy", "dek"],
  ru: ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

interface Parts {
  year: number;
  /** 1 to 12. */
  month: number;
  day: number;
  hour: string;
  minute: string;
  second: string;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

function partsOf(date: Date, timeZone: string): Parts {
  const found: Record<string, string> = {};
  for (const { type, value } of formatterFor(timeZone).formatToParts(date)) found[type] = value;
  return {
    year: Number(found["year"]),
    month: Number(found["month"]),
    day: Number(found["day"]),
    hour: found["hour"] ?? "00",
    minute: found["minute"] ?? "00",
    second: found["second"] ?? "00",
  };
}

function toDate(value: string | Date): Date | null {
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date;
}

function dayText(
  parts: Parts,
  today: Parts,
  language: Language,
  year: "other" | "always" = "other",
): string {
  const month = MONTHS[language][parts.month - 1] ?? String(parts.month);
  const day = language === "uz" ? `${parts.day}-${month}` : `${parts.day} ${month}`;
  return year === "other" && parts.year === today.year ? day : `${day} ${parts.year}`;
}

function sameDay(a: Parts, b: Parts): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

/** What could not be read as a moment is shown as it came. */
function unreadable(value: string | Date): string {
  return typeof value === "string" ? value : "";
}

/** `14:30` today, `2-okt, 14:30` another day, `2-okt 2025, 14:30` another year. */
export function formatTime(
  value: string | Date,
  language: Language,
  { now = new Date(), timeZone = MARKET_TIME_ZONE, year = "other" }: TimeOptions = {},
): string {
  const date = toDate(value);
  if (date === null) return unreadable(value);
  const parts = partsOf(date, timeZone);
  const today = partsOf(now, timeZone);
  const clock = `${parts.hour}:${parts.minute}`;
  // With the year always said, the day is said too: a year belongs to a day.
  return year === "other" && sameDay(parts, today)
    ? clock
    : `${dayText(parts, today, language, year)}, ${clock}`;
}

/** The time of day alone, whatever the day: `14:30`. */
export function formatClock(
  value: string | Date,
  { timeZone = MARKET_TIME_ZONE }: Pick<TimeOptions, "timeZone"> = {},
): string {
  const date = toDate(value);
  if (date === null) return unreadable(value);
  const parts = partsOf(date, timeZone);
  return `${parts.hour}:${parts.minute}`;
}

/** The time to the second, for a history: `14:02:10`, or `2-okt, 14:02:10`. */
export function formatTimeExact(
  value: string | Date,
  language: Language,
  { now = new Date(), timeZone = MARKET_TIME_ZONE }: TimeOptions = {},
): string {
  const date = toDate(value);
  if (date === null) return unreadable(value);
  const parts = partsOf(date, timeZone);
  const today = partsOf(now, timeZone);
  const clock = `${parts.hour}:${parts.minute}:${parts.second}`;
  return sameDay(parts, today) ? clock : `${dayText(parts, today, language)}, ${clock}`;
}

/** The day alone: `2-okt`, or `2-okt 2025` for another year. Today is a day too. */
export function formatDay(
  value: string | Date,
  language: Language,
  { now = new Date(), timeZone = MARKET_TIME_ZONE, year = "other" }: TimeOptions = {},
): string {
  const date = toDate(value);
  if (date === null) return unreadable(value);
  return dayText(partsOf(date, timeZone), partsOf(now, timeZone), language, year);
}

/**
 * A calendar date (`1985-03-12`, a birth date, the day a paper was given)
 * with its year, always: `12-mar 1985`. It names a day, not a moment, so no
 * clock moves it.
 */
export function formatDate(value: string, language: Language): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? formatDay(`${value}T12:00:00Z`, language, { timeZone: "UTC", year: "always" })
    : formatDay(value, language, { year: "always" });
}

/**
 * A visit's window: `14:00–16:00` today, `2-okt, 14:00–16:00` another day.
 * Without an end it is the start alone.
 */
export function formatWindow(
  start: string | Date,
  end: string | Date | null | undefined,
  language: Language,
  options: TimeOptions = {},
): string {
  const from = formatTime(start, language, options);
  const endDate = end == null ? null : toDate(end);
  const startDate = toDate(start);
  if (endDate === null || startDate === null) return from;
  const timeZone = options.timeZone ?? MARKET_TIME_ZONE;
  const to = partsOf(endDate, timeZone);
  // A window that ends on another day says which.
  const until = sameDay(to, partsOf(startDate, timeZone))
    ? `${to.hour}:${to.minute}`
    : formatTime(endDate, language, options);
  return `${from}–${until}`;
}

/**
 * How many of the market's days `value` is from today: 0 today, 1 tomorrow,
 * -1 yesterday. Null when it is not a moment.
 */
export function dayOffset(
  value: string | Date,
  { now = new Date(), timeZone = MARKET_TIME_ZONE }: TimeOptions = {},
): number | null {
  const date = toDate(value);
  if (date === null) return null;
  const a = partsOf(date, timeZone);
  const b = partsOf(now, timeZone);
  const days = (parts: Parts) => Date.UTC(parts.year, parts.month - 1, parts.day) / 86_400_000;
  return days(a) - days(b);
}

/** The hour of the day by the market's clock, 0 to 23; null when it is not a moment. */
export function hourOfDay(
  value: string | Date = new Date(),
  { timeZone = MARKET_TIME_ZONE }: Pick<TimeOptions, "timeZone"> = {},
): number | null {
  const date = toDate(value);
  return date === null ? null : Number(partsOf(date, timeZone).hour);
}

/**
 * A day and an hour as a `datetime-local` field holds them, as the instant
 * they are in a time zone: the catalog's times are the market's, whatever
 * the clock of the person's computer says.
 */
export function instantIn(local: string, timeZone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (match === null) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ];
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  if (Number.isNaN(asUtc)) return null;
  // The zone's distance from UTC at that moment: what its clock shows, read back as UTC.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(asUtc));
  const part = (type: string) => Number(parts.find((each) => each.type === type)?.value);
  const shown = Date.UTC(
    part("year"),
    part("month") - 1,
    part("day"),
    part("hour"),
    part("minute"),
    part("second"),
  );
  return new Date(asUtc - (shown - asUtc)).toISOString();
}

/**
 * A moment as a `datetime-local` field holds it, by a zone's clock: the
 * other way round from `instantIn`.
 */
export function localIn(value: string | Date, timeZone: string): string {
  const date = toDate(value);
  if (date === null) return "";
  const parts = partsOf(date, timeZone);
  const two = (number: number) => String(number).padStart(2, "0");
  return `${String(parts.year)}-${two(parts.month)}-${two(parts.day)}T${parts.hour}:${parts.minute}`;
}
